import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Paperclip, Eye, Download, Trash2 } from 'lucide-react';
import { cotizacionesAPI } from '../services/api';
import { Card, Button, useConfirm, Tooltip } from './ui';

/**
 * Anexos globales por HILO de cotizacion. Todas las versiones (COT-1, COT-1A,
 * COT-1B...) comparten los mismos anexos porque el backend usa el
 * cotizacion_root_id (padre_id o self si es raiz).
 *
 * Props:
 *   cotizacionId: id de cualquier version del hilo (raiz o version)
 *   readonly: si true, solo listar/ver/descargar (sin subir/eliminar)
 *
 * Sin cotizacionId → no carga nada (util cuando se muestra en la vista de
 * cotizacion nueva antes de que exista el id).
 */
const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const AnexosSection = ({ cotizacionId, readonly = false }) => {
    const confirm = useConfirm();
    const [anexos, setAnexos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    const cargar = useCallback(async () => {
        if (!cotizacionId) return;
        setLoading(true);
        try {
            const res = await cotizacionesAPI.listarAnexos(cotizacionId);
            setAnexos(res.data || []);
        } catch (err) {
            const status = err?.status;
            // 403/404 son flujos esperados (sin acceso / cotizacion inexistente).
            // Silenciamos para no disparar el dev overlay de React.
            if (status !== 403 && status !== 404) {
                console.error('Error cargando anexos:', err?.message || err);
            }
        } finally {
            setLoading(false);
        }
    }, [cotizacionId]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleSubir = async (files) => {
        // Snapshot inmediato — el input dispara e.target.value='' tras esta llamada
        // y el FileList original queda vacio antes de que await regrese.
        const arr = Array.from(files || []);
        if (arr.length === 0) return;
        if (!cotizacionId) {
            toast.error('Guarda la cotización primero para poder adjuntar anexos.');
            return;
        }
        setSubiendo(true);
        try {
            const res = await cotizacionesAPI.subirAnexos(cotizacionId, arr);
            setAnexos(res.data || []);
            const n = arr.length;
            toast.success(`${n} anexo${n > 1 ? 's' : ''} subido${n > 1 ? 's' : ''}`);
        } catch (err) {
            toast.error(err.message || 'No se pudieron subir los anexos');
        } finally {
            setSubiendo(false);
        }
    };

    const handleEliminar = async (anexo) => {
        const ok = await confirm({
            title: '¿Eliminar anexo?',
            message: <p>Se eliminará <strong>{anexo.archivo_nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            await cotizacionesAPI.eliminarAnexo(cotizacionId, anexo.id);
            setAnexos(prev => prev.filter(a => a.id !== anexo.id));
            toast.success('Anexo eliminado');
        } catch (err) {
            toast.error(err.message || 'No se pudo eliminar');
        }
    };

    if (!cotizacionId) {
        return (
            <Card className="mt-3.5">
                <Card.Header
                    title={readonly ? 'Anexos' : 'Anexos (opcional)'}
                    subtitle="Documentos del proyecto — se comparten entre todas las versiones de esta cotización."
                />
                <Card.Body>
                    <div className="empty-state" style={{ padding: 20, textAlign: 'center', color: 'rgb(var(--muted))' }}>
                        Guarda la cotización primero para poder agregar documentos.
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="mt-3.5">
            <Card.Header
                title={readonly ? 'Anexos' : 'Anexos (opcional)'}
                subtitle="Documentos del proyecto — se comparten entre todas las versiones de esta cotización."
            />
            <Card.Body>
                {loading ? (
                    <p className="text-sm text-muted">Cargando anexos…</p>
                ) : (
                    <>
                        {anexos.length > 0 && (
                            <div className="anexos-list">
                                {anexos.map(a => (
                                    <div key={a.id} className="anexo-item">
                                        <div className="anexo-info">
                                            <Paperclip size={16} className="anexo-icon" />
                                            <div className="anexo-meta">
                                                <strong className="anexo-nombre">{a.archivo_nombre}</strong>
                                                <span className="text-xs text-muted">
                                                    {formatSize(a.size_bytes)}
                                                    {a.subido_por_nombre ? ` · ${a.subido_por_nombre}` : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="anexo-actions">
                                            <Tooltip content="Ver">
                                                <Button
                                                    variant="ghost" size="sm"
                                                    leftIcon={<Eye size={14} />}
                                                    onClick={async () => {
                                                        try { await cotizacionesAPI.verAnexo(cotizacionId, a.id); }
                                                        catch (e) { toast.error(e.message); }
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip content="Descargar">
                                                <Button
                                                    variant="ghost" size="sm"
                                                    leftIcon={<Download size={14} />}
                                                    onClick={async () => {
                                                        try { await cotizacionesAPI.descargarAnexo(cotizacionId, a.id, a.archivo_nombre); }
                                                        catch (e) { toast.error(e.message); }
                                                    }}
                                                />
                                            </Tooltip>
                                            {!readonly && (
                                                <Tooltip content="Eliminar">
                                                    <Button
                                                        variant="ghost" size="sm"
                                                        leftIcon={<Trash2 size={14} />}
                                                        onClick={() => handleEliminar(a)}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!readonly && (
                            <label
                                className={`archivo-uploader ${dragActive ? 'is-drag' : ''} ${anexos.length > 0 ? 'mt-3' : ''}`}
                                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                                onDrop={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    setDragActive(false);
                                    if (subiendo) return;
                                    handleSubir(e.dataTransfer.files);
                                }}
                            >
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt,.csv,.zip"
                                    onChange={(e) => {
                                        handleSubir(e.target.files);
                                        e.target.value = '';
                                    }}
                                    disabled={subiendo}
                                    style={{ display: 'none' }}
                                />
                                <div className="archivo-uploader-icon">
                                    <Paperclip size={32} />
                                </div>
                                <strong className="archivo-uploader-title">
                                    {subiendo
                                        ? 'Subiendo…'
                                        : (dragActive ? 'Suelta los archivos aquí' : 'Arrastra archivos o haz clic para agregar')}
                                </strong>
                                <div className="archivo-uploader-hint">
                                    PDF · DOC · DOCX · XLS · XLSX · PNG · JPG · WEBP · TXT · CSV · ZIP
                                </div>
                                <div className="archivo-uploader-hint">Múltiples archivos · máximo 15MB c/u</div>
                            </label>
                        )}

                        {anexos.length === 0 && readonly && (
                            <p className="text-sm text-muted">Esta cotización no tiene anexos.</p>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default AnexosSection;
